import { Injectable } from '@angular/core';
import { TenantService } from 'src/app/tenant/tenant.service';

interface CachedLogo { image: string; refreshedAt: number; }

@Injectable({ providedIn: 'root' })
export class LoaderLogoCacheService {
  private readonly maxAge = 24 * 60 * 60 * 1000;
  private readonly maxBytes = 128 * 1024;
  private readonly memory = new Map<string, CachedLogo>();
  private readonly pending = new Map<string, Promise<string>>();

  constructor(private tenantService: TenantService) {}

  private key(tenant: string): string { return `church.loaderLogo.image.${tenant}`; }

  getCached(tenant: string): string {
    if (!this.memory.has(tenant)) {
      try {
        const entry = JSON.parse(localStorage.getItem(this.key(tenant)) || 'null');
        if (entry && /^data:image\/(png|jpeg|webp|gif);base64,/.test(entry.image)
          && entry.image.length < this.maxBytes * 2 && Number.isFinite(entry.refreshedAt)) {
          this.memory.set(tenant, entry);
        }
      } catch { /* Storage may be unavailable or contain an older cache format. */ }
    }
    return this.memory.get(tenant)?.image || '';
  }

  invalidate(tenant: string): void {
    this.memory.delete(tenant);
    try { localStorage.removeItem(this.key(tenant)); } catch { }
  }

  refresh(tenant: string): Promise<string> {
    const cached = this.getCached(tenant);
    if (cached && Date.now() - this.memory.get(tenant).refreshedAt < this.maxAge) {
      return Promise.resolve(cached);
    }
    const existing = this.pending.get(tenant);
    if (existing) return existing;
    const request = this.download(tenant).finally(() => this.pending.delete(tenant));
    this.pending.set(tenant, request);
    return request;
  }

  private async download(tenant: string): Promise<string> {
    let url = '';
    try {
      url = await this.tenantService.getTenantSmallLogo(tenant);
      if (!url) return this.getCached(tenant);
      const response = await fetch(url, { cache: 'reload' });
      if (!response.ok) return this.getCached(tenant) || url;
      const blob = await response.blob();
      if (blob.size > this.maxBytes || !/^image\/(png|jpeg|webp|gif)$/.test(blob.type)) {
        return this.getCached(tenant) || url;
      }
      const image = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
      const entry = { image, refreshedAt: Date.now() };
      this.memory.set(tenant, entry);
      try { localStorage.setItem(this.key(tenant), JSON.stringify(entry)); } catch { }
      return image;
    } catch {
      // Cross-origin images without CORS can still be displayed using their URL.
      return this.getCached(tenant) || url;
    }
  }
}
